import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { PRICE_CHANGE_FILTER_OPTIONS_LIMIT, PRICE_CHANGE_FILTER_OPTIONS_MAX_OFFSET } from "../constants"

import type { Lookup, LookupQueriesCompaniesArgs, Query } from "@/graphql/codegen/graphql"
import { GetCompaniesLookupDocument } from "@/graphql/codegen/graphql"
import { CUSTOMER_COMPANY_TYPES } from "@/types/companyType"

interface UseCustomersLookupReturn {
  customers: Lookup[]
  isFetching: boolean
  error?: CombinedError
}

export const useCustomersLookup = ({
  companyIds,
}: {
  limit?: number
  companyIds?: string[]
}): UseCustomersLookupReturn => {
  const [customers, setCustomers] = useState<Lookup[]>([])
  const [offset, setOffset] = useState(0)

  const [{ data, fetching, error }] = useQuery<Pick<Query, "lookup">, LookupQueriesCompaniesArgs>({
    query: GetCompaniesLookupDocument,
    variables: {
      input: {
        filter: {
          types: CUSTOMER_COMPANY_TYPES as string[],
          ...(companyIds && companyIds?.length && { companyIds }),
        },
        limit: PRICE_CHANGE_FILTER_OPTIONS_LIMIT,
        offset,
      },
    },
  })

  useEffect(() => {
    if (data?.lookup.companies.__typename === "LookupSuccess") {
      const paginatedCompanies = data.lookup.companies.items

      setCustomers((prev: Lookup[]) => {
        const customers = offset === 0 ? [...paginatedCompanies] : [...prev, ...paginatedCompanies]

        return customers.sort((a, b) => a.label.localeCompare(b.label))
      })

      if (
        paginatedCompanies.length === PRICE_CHANGE_FILTER_OPTIONS_MAX_OFFSET &&
        offset <= PRICE_CHANGE_FILTER_OPTIONS_MAX_OFFSET
      ) {
        setOffset((prevOffset) => prevOffset + PRICE_CHANGE_FILTER_OPTIONS_MAX_OFFSET)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return {
    customers,
    isFetching: fetching,
    error,
  }
}
